import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Bell, Mail, Clock, AlertTriangle, Ban, type LucideIcon } from 'lucide-react';
import { formatMessageTime } from '../../shared/lib/format';
import { useAuth } from '../../auth/useAuth';
import { useSetActiveSubscription } from '../subscriptions/api';
import {
  clearNotifications,
  markAllRead,
  setNotificationOwner,
  useNotifications,
  useUnreadCount,
  type AppNotification,
} from './store';
import { useNotificationBootstrap } from './useNotificationBootstrap';
import { useNotificationOwner } from './useNotificationOwner';
import { useNotificationStream } from './useNotificationStream';

interface NotificationBellProps {
  /** Подписываться на SSE-поток только для аутентифицированного пользователя. */
  enabled: boolean;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const LIFECYCLE_ICONS: Record<string, LucideIcon> = {
  'mailbox.expiring': Clock,
  'mailbox.size.threshold': AlertTriangle,
  // Предупреждение и исчерпание различаются иконкой намеренно: в первом случае приём писем
  // продолжается, во втором остановлен, и спутать их значит спутать «можно успеть» с «уже поздно».
  'subscription.quota.warning': AlertTriangle,
  'subscription.quota.reached': Ban,
};

/** Уведомление относится к сообщению (кликабельно к письму), а не к lifecycle-событию. */
function isMessageNotification(n: AppNotification): boolean {
  return n.type === 'message.received' && !!n.messageId && n.messageId !== EMPTY_GUID;
}

/** Колокольчик уведомлений: бейдж непрочитанных + выпадающий список последних писем. */
export function NotificationBell({ enabled }: NotificationBellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const setActive = useSetActiveSubscription();
  const activeSubscriptionId = profile?.activeSubscriptionId ?? null;
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Синхронизируем стор с владельцем (пользователем): чужая сохранённая история
  // отбрасывается, своя — восстанавливается. НЕ трогает уведомления текущей сессии.
  const { owner, ready } = useNotificationOwner();
  useEffect(() => {
    if (!ready) return;
    setNotificationOwner(owner);
  }, [ready, owner]);

  // Живые обновления (счётчики писем + новые уведомления) идут по SSE и зависят ТОЛЬКО
  // от факта авторизации — не блокируются загрузкой профиля/подписок. При смене активной
  // подписки поток пересоздаётся, чтобы охватить аккаунт новой подписки.
  useNotificationBootstrap(enabled ? owner : null);
  useNotificationStream(enabled, activeSubscriptionId);
  const notifications = useNotifications();
  const unread = useUnreadCount();

  // Клик по уведомлению: если письмо в другой подписке — сначала переключаем её,
  // затем открываем сообщение (§2). Ошибку переключения игнорируем и всё равно переходим.
  const openNotification = (n: AppNotification) => {
    setOpen(false);
    const go = () => {
      // Уведомление о письме → открываем письмо; lifecycle-событие → открываем ящик
      // (или список ящиков, если ящик неизвестен, напр. deactivate-all по квоте).
      if (isMessageNotification(n)) {
        void navigate({
          to: '/mailbox/$mailboxId/message/$messageId',
          params: { mailboxId: n.mailboxId, messageId: n.messageId },
        });
      } else if (n.mailboxId && n.mailboxId !== EMPTY_GUID) {
        void navigate({ to: '/mailbox/$mailboxId', params: { mailboxId: n.mailboxId } });
      } else {
        void navigate({ to: '/me' });
      }
    };
    if (n.accountId && activeSubscriptionId && n.accountId !== activeSubscriptionId) {
      setActive.mutate(n.accountId, { onSuccess: go, onError: go });
    } else {
      go();
    }
  };

  // Иконка + человекочитаемый текст (i18n) по типу уведомления.
  const describe = (n: AppNotification): { Icon: LucideIcon; title: string; body: string } => {
    if (n.type === 'message.received') {
      return {
        Icon: Mail,
        title: n.from,
        body: n.subject && n.subject.length > 0 ? n.subject : t('notifications.noSubject'),
      };
    }
    const Icon = LIFECYCLE_ICONS[n.type] ?? Bell;
    const title = t(`notifications.lifecycle.${n.type}.title`, { defaultValue: n.from });
    const body = t(`notifications.lifecycle.${n.type}.body`, {
      defaultValue: n.subject ?? '',
      address: n.from,
      minutes: n.minutes ?? 0,
      percent: n.percent ?? 0,
      used: n.usedMb ?? 0,
      limit: n.limitMb ?? 0,
      // Остаток считаем здесь, а не тянем в событии: он однозначно выводится из двух других чисел,
      // и лишнее поле в контракте пришлось бы держать согласованным с ними.
      remaining: Math.max(0, (n.limitMb ?? 0) - (n.usedMb ?? 0)),
    });
    return { Icon, title, body };
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) markAllRead();
      return next;
    });
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label={t('notifications.title')}
        onClick={toggle}
        className="relative grid h-9 w-9 place-items-center rounded-full bg-page text-fg-muted hover:text-fg"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-80 overflow-hidden rounded-xl border border-border bg-surface shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <span className="text-sm font-semibold text-fg">{t('notifications.title')}</span>
            {notifications.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  clearNotifications();
                }}
                className="text-xs text-fg-muted hover:text-brand"
              >
                {t('notifications.clear')}
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-fg-muted">
              {t('notifications.empty')}
            </p>
          ) : (
            <ul className="max-h-96 divide-y divide-border overflow-y-auto">
              {notifications.map((n) => {
                const { Icon, title, body } = describe(n);
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => {
                        openNotification(n);
                      }}
                      className="flex w-full gap-3 px-4 py-3 text-left hover:bg-page"
                    >
                      <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-page text-brand">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium text-fg">{title}</span>
                          <span className="shrink-0 text-xs text-fg-muted">
                            {formatMessageTime(n.createdAt)}
                          </span>
                        </span>
                        <span className="block truncate text-sm text-fg-muted">{body}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
